import './App.css';
import { useCallback, useEffect, useState } from 'react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import AdminUsersView from './components/AdminUsersView';
import SupportMailsView from './components/SupportMailsView';
import PromoCodesView from './components/PromoCodesView';

const TOKEN_KEY = 'adminToken';
const MENU_ITEMS = [
  { key: 'administrators', label: 'Administrators', component: AdminUsersView },
  { key: 'support-mails', label: 'Support e-mails', component: SupportMailsView },
  { key: 'promo-codes', label: 'Promo codes', component: PromoCodesView },
];

function AppContent() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);
  const [meError, setMeError] = useState(null);
  const [activeMenu, setActiveMenu] = useState(MENU_ITEMS[0].key);

  const loadMe = useCallback(async (t) => {
    setMeError(null);
    const res = await fetch('/api/admin/me', {
      headers: { Authorization: `Bearer ${t}` },
    });
    if (!res.ok) {
      setMeError('Session expired or invalid.');
      localStorage.removeItem(TOKEN_KEY);
      setToken(null);
      setUser(null);
      return;
    }
    setUser(await res.json());
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
    setMeError(null);
  }, []);

  useEffect(() => {
    if (!token) {
      setUser(null);
      return;
    }
    loadMe(token);
  }, [token, loadMe]);

  const onGoogleSuccess = async (credentialResponse) => {
    const res = await fetch('/api/admin/auth/google', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credential: credentialResponse.credential }),
    });
    if (res.status === 403) {
      window.alert('This Google account is not allowed to access the console.');
      return;
    }
    if (!res.ok) {
      window.alert('Login failed.');
      return;
    }
    const data = await res.json();
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
  };

  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  if (!clientId) {
    return (
      <div className="App">
        <p className="auth-hint">Set REACT_APP_GOOGLE_CLIENT_ID (same as server googleAuth.clientId).</p>
      </div>
    );
  }

  const activeMenuItem = MENU_ITEMS.find((item) => item.key === activeMenu) || MENU_ITEMS[0];
  const ActiveView = activeMenuItem.component;

  return (
    <GoogleOAuthProvider clientId={clientId}>
      <div className="App">
        {!token && (
          <header className="App-header">
            <h1>Server console for Det application.</h1>
            <div className="auth-panel">
              <p className="auth-hint">Sign in with a Google account that exists in AdminUser.</p>
              <GoogleLogin onSuccess={onGoogleSuccess} onError={() => window.alert('Google sign-in failed.')} useOneTap />
            </div>
          </header>
        )}
        {token && (
          <main className="admin-layout">
            <aside className="sidebar">
              <h2 className="sidebar-title">Menu</h2>
              <nav className="menu-list">
                {MENU_ITEMS.map((item) => (
                  <button
                    type="button"
                    key={item.key}
                    className={`menu-item ${activeMenu === item.key ? 'active' : ''}`}
                    onClick={() => setActiveMenu(item.key)}
                  >
                    {item.label}
                  </button>
                ))}
              </nav>
              <div className="sidebar-footer">
                {user && <p className="sidebar-user">{user.name || user.googleId}</p>}
                {meError && <p className="auth-error">{meError}</p>}
                {!user && !meError && <p className="auth-hint">Loading profile…</p>}
                <button type="button" className="sign-out" onClick={logout}>
                  Sign out
                </button>
              </div>
            </aside>
            <section className="content">
              <h1>{activeMenuItem.label}</h1>
              <ActiveView token={token} onUnauthorized={logout} />
            </section>
          </main>
        )}
      </div>
    </GoogleOAuthProvider>
  );
}

function App() {
  return <AppContent />;
}

export default App;
